import { BusyWatcher, watchBusy } from "@frui.ts/screens";
import { ScreenBase } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import { action, computed, observable } from "mobx";
import LocalizationService from "services/localizationService";
import { Modules } from "config";
import { formatLicensePlate } from "utils/localizations";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import type Vehicle from "entities/vehicleResponse";
import type VehicleRegisterVehicleFuelType from "entities/vehicleRegisterVehicleFuelType";
import type VehicleRegisterVehicleClassifiedFuelType from "entities/vehicleRegisterVehicleClassifiedFuelType";
import type VehicleRegisterVehicleStatus from "entities/vehicleRegisterVehicleStatus";
import type VehicleRegisterVehiclePropertyChangeDisplayType from "entities/vehicleRegisterVehiclePropertyChangeDisplayType";
import type VehicleRegisterVehicleChangeOrigin from "entities/vehicleRegisterVehicleChangeOrigin";
import VehicleRegisterRepository from "repositories/vehicleRegisterRepository";
import * as Sentry from "@sentry/react";
import { bound } from "@frui.ts/helpers";
import VehicleRegisterVehicleSynchronizationResponse from "entities/vehicleRegisterVehicleSynchronizationResponse";
import type VehicleRegisterVehicleSynchronizationChangeResponse from "entities/vehicleRegisterVehicleSynchronizationChangeResponse";
import EventBus, { vehicleSynchronizationFetchFlag } from "services/eventBus";
import { vehicleSynchronizationOpenDetailRSV } from "services/eventBus";
import VehicleRegisterVehicleSynchronizationStatus from "entities/vehicleRegisterVehicleSynchronizationStatus";
import EnumService from "services/enumService";

export default class VehicleSynchronizationViewModel extends ScreenBase {
  @observable step = 1;
  @observable isFetched = false;
  @observable busyWatcher = new BusyWatcher();
  @observable vehicleRegisterSyncChanges: VehicleRegisterVehicleSynchronizationResponse;
  private unsubscriptions: Array<() => void> = [];

  constructor(
    public vehicleRegister: VehicleRegisterVehicle,
    public isNextStep: boolean,
    protected eventBus: EventBus,
    public localizationService: LocalizationService,
    public enumService: EnumService,
    protected vehicleRegisterRepository: VehicleRegisterRepository
  ) {
    super();
    this.navigationName = "";
  }

  protected onInitialize(): Promise<any> | void {
    if (this.isNextStep) {
      this.onContinue();
    }
    return super.onInitialize();
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "vehicleSynchronization.title");
  }

  @computed
  get updatedAt() {
    return this.vehicleRegisterSyncChanges?.synchronizedAt
      ? this.vehicleRegisterSyncChanges?.synchronizedAt
      : this.vehicleRegister?.updatedAt;
  }

  @computed
  get secondStep() {
    return this.step === 2;
  }

  @computed
  get transformedItems(): VehicleRegisterVehicleSynchronizationChangeResponse[] {
    return this.vehicleRegisterSyncChanges?.items.flatMap(item => {
      return item;
    });
  }
  @computed
  get changes(): boolean {
    return (
      this.vehicleRegisterSyncChanges &&
      this.vehicleRegisterSyncChanges.items.length > 0 &&
      this.vehicleRegisterSyncChanges.synchronizationStatus === VehicleRegisterVehicleSynchronizationStatus.Successful
    );
  }

  @computed
  get disabledRSV(): boolean {
    return (
      this.vehicleRegisterSyncChanges &&
      this.vehicleRegisterSyncChanges.synchronizationStatus === VehicleRegisterVehicleSynchronizationStatus.Disabled
    );
  }

  @computed
  get failedSync(): boolean {
    return (
      this.vehicleRegisterSyncChanges &&
      this.vehicleRegisterSyncChanges.synchronizationStatus === VehicleRegisterVehicleSynchronizationStatus.Failed
    );
  }

  @computed
  get noChanges(): boolean {
    return (
      this.vehicleRegisterSyncChanges &&
      this.vehicleRegisterSyncChanges.items.length === 0 &&
      this.vehicleRegisterSyncChanges.synchronizationStatus === VehicleRegisterVehicleSynchronizationStatus.Successful
    );
  }

  @computed
  get vehicleRegisterData() {
    return this.vehicleRegister;
  }

  @computed
  get licensePlate() {
    const vehicle: Vehicle = {
      licensePlate: this.vehicleRegister.licensePlate,
      countryId: this.vehicleRegister.countryId,
    };
    return this.localizationService
      .translateModule(Modules.callCenter, "vehicleSynchronization.licensePlateTitle")
      .replace("%licensePlate%", formatLicensePlate(vehicle));
  }

  @bound
  @watchBusy
  async loadChanges() {
    return await this.vehicleRegisterRepository.getVehicleSynchronizations(this.vehicleRegister.vehicleId);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleSynchronization.${code}`, size);
  }

  translateCountry(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? "";
  }

  formatCountry(countryId: string) {
    return `${this.translateCountry(countryId)} - ${this.translateCountryName(countryId)}`;
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateBoolean(boolean: string) {
    return this.localizationService.translateGeneral(`boolean.${boolean.toLowerCase()}`);
  }

  translateVehicleRegisterVehicleFuelType(fuelType: string) {
    const fuelTypes = fuelType.split(",") as VehicleRegisterVehicleFuelType[];
    const translation = fuelTypes.map(f => this.localizationService.translateEnum("vehicleRegisterVehicleFuelType", f));
    return translation.length > 1 ? translation.join(", ") : translation[0];
  }

  translateVehicleRegisterVehicleClassifiedFuelType(classifiedFuelType: VehicleRegisterVehicleClassifiedFuelType) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleClassifiedFuelType", classifiedFuelType);
  }

  translateVehicleStatus(status: VehicleRegisterVehicleStatus) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleStatus", status);
  }

  translateChangeType(status: VehicleRegisterVehiclePropertyChangeDisplayType) {
    return this.localizationService.translateEnum("vehicleRegisterVehiclePropertyChangeDisplayType", status);
  }

  translateChangeOrigin(status: VehicleRegisterVehicleChangeOrigin) {
    return this.localizationService.translateEnum("vehicleRegisterVehicleChangeOrigin", status);
  }

  formatHeight(amount: number) {
    return `${amount} kg`;
  }

  parseDateTime(dateTime: string) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [date, time] = dateTime.split(" ");
    return date;
  }

  @action.bound
  onClose() {
    this.requestClose();
  }

  @bound
  async onContinue() {
    if (this.step === 1) {
      this.step = 2;
      if (this.vehicleRegister.vehicleId && !this.isFetched) {
        try {
          const data = await this.loadChanges();
          if (data) {
            this.vehicleRegisterSyncChanges = data as VehicleRegisterVehicleSynchronizationResponse;
          }
          this.isFetched = true;
        } catch (e) {
          Sentry.captureException(e);
          this.isFetched = false;
        }
      }
      this.eventBus.publish(vehicleSynchronizationFetchFlag({ fetchSearch: this.vehicleRegisterSyncChanges.items.length > 0 }));
      return;
    }

    this.eventBus.publish(vehicleSynchronizationOpenDetailRSV({ openDetail: true, vehicleId: this.vehicleRegister.vehicleId }));
  }

  static Factory({ container }: interfaces.Context) {
    return (vehicleRegister: VehicleRegisterVehicle, isNextStep = false) =>
      new VehicleSynchronizationViewModel(
        vehicleRegister,
        isNextStep,
        container.get(EventBus),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(VehicleRegisterRepository)
      );
  }
}
